import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { DataTable } from "../components/table/DataTable";
import TraceabilityReport from "./TraceabilityReport";
import dayjs from "dayjs";
import PageLayout from '../components/ui/PageLayout';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tabs, { TabPanel } from '../components/ui/Tabs';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import LogoSpinner from '../components/ui/LogoSpinner';
import Pager from '../components/ui/Pager';
import { Panel, StatTile } from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';

const TableLoading = ({ label }) => (
  <SkeletonGroup label={label} className="space-y-1 p-3">
    {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
  </SkeletonGroup>
);

const BOARD_TYPES = ["AP", "ESC", "BAT", "CHG", "LED", "GPS"];
const BOARD_TYPE_OPTIONS = [{ key: "", label: "All Board Types" }, ...BOARD_TYPES.map((type) => ({ key: type, label: type }))];

// Module scope so the two tables' cells can call it: it closes over nothing.
const formatDate = (date) => dayjs(date).format("MM/DD/YY HH:mm");

// ---------------------------------------------------------------------------
// The two history tables, as shared-DataTable columns.
//
// Module scope per the DataTable contract: the arrays never change identity and
// every cell is pure over its row, so neither table needs `meta`.
//
// No `sortable` and no accessorFn on either. Both are PAGES of a server-side
// list — offset/limit lives on the page below, and only one page of rows is ever
// in state, so a client sort would reorder 25 rows out of thousands and read as
// if it had sorted the lot.
// ---------------------------------------------------------------------------

const OPERATION_COLUMNS = [
  {
    id: "time",
    header: "Time",
    meta: { numeric: true, cellClass: "text-on-surface-variant whitespace-nowrap" },
    cell: ({ row }) => formatDate(row.original.operation_timestamp),
  },
  {
    id: "board_type",
    header: "Type",
    cell: ({ row }) => (
      <span className="px-2 py-1 bg-gray-700 rounded text-xs">{row.original.board_type}</span>
    ),
  },
  {
    id: "station",
    header: "Station",
    meta: { cellClass: "font-mono text-xs whitespace-nowrap" },
    cell: ({ row }) => row.original.station_id,
  },
  {
    id: "operator",
    header: "Operator",
    cell: ({ row }) => (
      <>{row.original.operator_firstname} {row.original.operator_lastname?.charAt(0)}.</>
    ),
  },
  {
    id: "serial",
    header: "Serial Number",
    meta: { cellClass: "whitespace-nowrap" },
    cell: ({ row }) =>
      row.original.drone_serialnumber ? (
        <a href={`/drone/${row.original.drone_serialnumber}`} className="text-primary hover:underline font-mono text-xs">
          {row.original.drone_serialnumber}
        </a>
      ) : (
        <span className="text-on-surface-variant">-</span>
      ),
  },
  {
    id: "firmware",
    header: "Firmware",
    meta: { cellClass: "font-mono text-xs text-on-surface-variant" },
    cell: ({ row }) => row.original.firmware_file,
  },
  {
    id: "result",
    header: "Result",
    cell: ({ row }) =>
      row.original.success ? (
        <span className="text-green-400">Success</span>
      ) : (
        <span className="text-red-400" title={row.original.error_message}>Failed</span>
      ),
  },
  {
    id: "duration",
    header: "Duration",
    meta: { cellClass: "text-on-surface-variant" },
    cell: ({ row }) => (row.original.duration_seconds ? `${row.original.duration_seconds}s` : '-'),
  },
];

const BOARD_COLUMNS = [
  {
    id: "created",
    header: "Created",
    meta: { numeric: true, cellClass: "text-on-surface-variant whitespace-nowrap" },
    cell: ({ row }) => formatDate(row.original.created_at),
  },
  {
    id: "board_type",
    header: "Type",
    cell: ({ row }) => (
      <span className="px-2 py-1 bg-gray-700 rounded text-xs">{row.original.board_type}</span>
    ),
  },
  {
    id: "batch",
    header: "Batch",
    meta: { cellClass: "text-xs" },
    cell: ({ row }) => row.original.batch_number || <span className="text-on-surface-variant">-</span>,
  },
  {
    id: "primary_uid",
    header: "Primary UID",
    meta: { cellClass: "font-mono text-xs whitespace-nowrap" },
    cell: ({ row }) => row.original.primary_uid,
  },
  {
    id: "secondary_uid",
    header: "Secondary UID",
    meta: { cellClass: "font-mono text-xs text-on-surface-variant whitespace-nowrap" },
    cell: ({ row }) => row.original.secondary_uid || '-',
  },
  {
    id: "serial",
    header: "Serial Number",
    meta: { cellClass: "whitespace-nowrap" },
    cell: ({ row }) =>
      row.original.serialnumber ? (
        <a href={`/drone/${row.original.serialnumber}`} className="text-primary hover:underline">
          {row.original.serialnumber}
        </a>
      ) : (
        <span className="text-on-surface-variant">-</span>
      ),
  },
  {
    id: "created_by",
    header: "Created By",
    cell: ({ row }) => (
      <>{row.original.created_by_firstname} {row.original.created_by_lastname?.charAt(0)}.</>
    ),
  },
];

const FlashOperations = () => {
  const api = useApi();
  const toast = useToast();

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Operations list
  const [operations, setOperations] = useState([]);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsTotal, setOpsTotal] = useState(0);
  const [opsOffset, setOpsOffset] = useState(0);
  const [opsLimit] = useState(25);

  // Boards list
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [boardsTotal, setBoardsTotal] = useState(0);
  const [boardsOffset, setBoardsOffset] = useState(0);
  const [boardsLimit] = useState(25);

  // Filters
  const [boardTypeFilter, setBoardTypeFilter] = useState("");
  const [successFilter, setSuccessFilter] = useState("");
  const [stationFilter, setStationFilter] = useState("");

  // Tab state
  const [activeTab, setActiveTab] = useState("stats"); // stats, operations, boards

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await api.get("/flasher/admin/stats");
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load flash statistics");
    } finally {
      setStatsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch operations
  const fetchOperations = useCallback(async () => {
    setOpsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: opsLimit,
        offset: opsOffset,
      });
      if (boardTypeFilter) params.append("board_type", boardTypeFilter);
      if (successFilter) params.append("success", successFilter);
      if (stationFilter) params.append("station_id", stationFilter);

      const data = await api.get(`/flasher/admin/operations?${params}`);
      setOperations(data.operations || []);
      setOpsTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching operations:", error);
      toast.error("Failed to load operations");
    } finally {
      setOpsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opsLimit, opsOffset, boardTypeFilter, successFilter, stationFilter]);

  // Fetch boards
  const fetchBoards = useCallback(async () => {
    setBoardsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: boardsLimit,
        offset: boardsOffset,
      });
      if (boardTypeFilter) params.append("board_type", boardTypeFilter);

      const data = await api.get(`/flasher/admin/boards?${params}`);
      setBoards(data.boards || []);
      setBoardsTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching boards:", error);
      toast.error("Failed to load boards");
    } finally {
      setBoardsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsLimit, boardsOffset, boardTypeFilter]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Load operations when tab changes or filters change
  useEffect(() => {
    if (activeTab === "operations") {
      fetchOperations();
    }
  }, [activeTab, fetchOperations]);

  // Load boards when tab changes
  useEffect(() => {
    if (activeTab === "boards") {
      fetchBoards();
    }
  }, [activeTab, fetchBoards]);

  // Reset offset when filters change
  useEffect(() => {
    setOpsOffset(0);
    setBoardsOffset(0);
  }, [boardTypeFilter, successFilter, stationFilter]);

  const renderStats = () => {
    if (statsLoading) return <LogoSpinner label="Loading statistics" />;
    if (!stats) return <EmptyState variant="area">No statistics available</EmptyState>;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Total Boards" value={stats.total_boards} tone="accent" />
          <StatTile label="Total Operations" value={stats.total_operations} />
          <StatTile
            label="Success Rate"
            value={`${stats.success_rate}%`}
            tone={stats.success_rate >= 95 ? 'good' : stats.success_rate >= 80 ? 'warn' : 'danger'}
          />
          <StatTile label="Today" value={stats.operations_today} />
        </div>

        {/* Board Types Breakdown */}
        <Panel title="Boards by Type" level={3}>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {BOARD_TYPES.map((type) => (
              <StatTile key={type} on="panel" size="sm" label={type} value={stats.by_board_type[type] || 0} />
            ))}
          </div>
        </Panel>

        {/* Station Activity */}
        {Object.keys(stats.by_station).length > 0 && (
          <Panel title="Operations by Station" level={3}>
            <ListStack
              on="panel"
              label="Operations by station"
              items={Object.entries(stats.by_station)}
              getKey={([station]) => station}
              renderRow={([station, count]) => (
                <ListRow title={station} trailing={<span className="font-mono">{count}</span>} />
              )}
            />
          </Panel>
        )}

        {/* Recent Failures */}
        {stats.recent_failures?.length > 0 && (
          <Panel title="Recent Failures" level={3}>
            <ListStack
              on="panel"
              label="Recent failures"
              items={stats.recent_failures}
              getKey={(failure) => String(failure.id)}
              renderRow={(failure) => (
                <ListRow tone="danger">
                  <div className="flex justify-between items-start text-sm">
                    <div>
                      <span className="text-on-surface-variant">{failure.station_id}</span>
                      <span className="mx-2">|</span>
                      <span className="text-on-surface">{failure.board_type}</span>
                      <span className="mx-2">|</span>
                      <span className="font-mono text-xs text-on-surface-variant">{failure.primary_uid?.slice(0, 12)}...</span>
                    </div>
                    <div className="text-right">
                      <div className="text-on-surface-variant text-xs">{formatDate(failure.operation_timestamp)}</div>
                      {failure.error_message && (
                        <div className="text-red-400 text-xs truncate max-w-xs">{failure.error_message}</div>
                      )}
                    </div>
                  </div>
                </ListRow>
              )}
            />
          </Panel>
        )}
      </div>
    );
  };

  const renderOperations = () => {
    return (
      <div className="space-y-4">
        {/* Filters. Page-local, and OUTSIDE the loading state on purpose: the
            spinner used to replace this whole block, so every keystroke in
            Station ID unmounted the input it was typed into and took the focus
            with it. The vessel carries the spinner now and the filters stay put. */}
        <div className="flex flex-wrap gap-3 items-center">
          <Select
            label="Board type"
            width="fit"
            value={boardTypeFilter}
            onChange={setBoardTypeFilter}
            options={BOARD_TYPE_OPTIONS}
          />
          <Select
            label="Result"
            width="fit"
            value={successFilter}
            onChange={setSuccessFilter}
            options={[
              { key: '', label: 'All Results' },
              { key: 'true', label: 'Success' },
              { key: 'false', label: 'Failed' },
            ]}
          />
          <Field
            type="text"
            aria-label="Station ID"
            placeholder="Station ID"
            value={stationFilter}
            onChange={(e) => setStationFilter(e.target.value)}
            className="!w-32"
          />
        </div>

        {/* The count moves onto the vessel's strip, where it sits against the
            pile it counts instead of floating at the end of the filter row. */}
        <DataTable
          data={operations}
          columns={OPERATION_COLUMNS}
          getRowId={(op) => String(op.id)}
          loading={opsLoading}
          loadingContent={<TableLoading label="Loading operations" />}
          strip={<span className="text-on-surface-variant text-sm ml-auto">{opsTotal} total</span>}
          empty={
            <EmptyState variant="area">
              {boardTypeFilter || successFilter || stationFilter
                ? 'No operations match these filters.'
                : 'No flash operations recorded yet.'}
            </EmptyState>
          }
        />

        {/* Pagination */}
        <Reveal show={opsTotal > opsLimit}>
          <Pager
            align="center"
            label="Pages of operations"
            page={Math.floor(opsOffset / opsLimit)}
            pageCount={Math.ceil(opsTotal / opsLimit)}
            total={opsTotal}
            pageSize={opsLimit}
            busy={opsLoading}
            onPage={(page) => setOpsOffset(page * opsLimit)}
          />
        </Reveal>
      </div>
    );
  };

  const renderBoards = () => {
    return (
      <div className="space-y-4">
        {/* Filters. Page-local, and outside the loading state for the same
            reason the operations filters are. */}
        <div className="flex flex-wrap gap-3 items-center">
          <Select
            label="Board type"
            width="fit"
            value={boardTypeFilter}
            onChange={setBoardTypeFilter}
            options={BOARD_TYPE_OPTIONS}
          />
        </div>

        <DataTable
          data={boards}
          columns={BOARD_COLUMNS}
          getRowId={(board) => String(board.id)}
          loading={boardsLoading}
          loadingContent={<TableLoading label="Loading boards" />}
          strip={<span className="text-on-surface-variant text-sm ml-auto">{boardsTotal} total</span>}
          empty={
            <EmptyState variant="area">
              {boardTypeFilter
                ? 'No boards match this filter.'
                : 'No boards recorded yet.'}
            </EmptyState>
          }
        />

        {/* Pagination */}
        <Reveal show={boardsTotal > boardsLimit}>
          <Pager
            align="center"
            label="Pages of boards"
            page={Math.floor(boardsOffset / boardsLimit)}
            pageCount={Math.ceil(boardsTotal / boardsLimit)}
            total={boardsTotal}
            pageSize={boardsLimit}
            busy={boardsLoading}
            onPage={(page) => setBoardsOffset(page * boardsLimit)}
          />
        </Reveal>
      </div>
    );
  };

  return (
    <PageLayout kind="workspace" className="pt-4">
      <h1 className="text-2xl font-bold text-primary mb-6">Flash Operations</h1>

      <Tabs
        label="Flash operations sections"
        id="flash-ops"
        className="mb-6"
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { key: "stats", label: "Statistics" },
          { key: "operations", label: "Operations" },
          { key: "boards", label: "Boards" },
          { key: "report", label: "Report" },
        ]}
      />

      {/* Tab Content */}
      <TabPanel id="flash-ops" value={activeTab}>
        <FadeIn key={activeTab}>
          {activeTab === "stats" && renderStats()}
          {activeTab === "operations" && renderOperations()}
          {activeTab === "boards" && renderBoards()}
          {activeTab === "report" && <TraceabilityReport />}
        </FadeIn>
      </TabPanel>
    </PageLayout>
  );
};

export default FlashOperations;
